/**
 * Dataverse team membership helpers for the role-sync flow.
 *
 * `grantRoleWithTeamSync` writes the vba_userroleassignment row and adds
 * the user to the mapped Dataverse team. `revokeRoleWithTeamSync` removes
 * the row and — unless the user still holds another role for the same
 * team — removes them from the team.
 *
 * Team sync is best-effort: a failure writes a warning to console but
 * doesn't abort the role change (the UI already considers the role
 * granted once the vba_userroleassignment row is in place).
 */

import { dataverseClient } from '@shared/api';
import type { TableName } from '@shared/api/dataverse/types';
import {
  VERA_APP_ROLES,
  teamForRole,
  type VeraTeamName,
} from '@shared/auth';
import type { CreateUserRoleAssignmentInput, UserRoleAssignment } from '../model/types';
import { userRoleAssignmentApi } from './userRoleAssignmentApi';

interface TeamRow {
  teamid: string;
  name?: string;
}

function escape(s: string): string {
  return s.replace(/'/g, "''");
}

async function findTeamByName(name: string): Promise<TeamRow | null> {
  const result = await dataverseClient.list<TeamRow>('teams', {
    filter: `name eq '${escape(name)}'`,
    select: ['teamid', 'name'],
    top: 1,
  });
  return result.value[0] ?? null;
}

// The legacy SDK actions AddMembersTeam / RemoveMembersTeam aren't exposed
// on the Web API surface in this Dataverse environment (404). Manage team
// membership via the OData N:N navigation property instead.
const TEAMS_SET = 'teams' as TableName;
const SYSTEMUSERS_SET = 'systemusers' as TableName;
const NAV = 'teammembership_association';

async function addSystemuserToTeam(teamid: string, systemuserid: string): Promise<void> {
  await dataverseClient.associate(TEAMS_SET, teamid, NAV, SYSTEMUSERS_SET, systemuserid);
}

async function removeSystemuserFromTeam(teamid: string, systemuserid: string): Promise<void> {
  await dataverseClient.disassociate(TEAMS_SET, teamid, NAV, systemuserid);
}

/**
 * Does the user still hold at least one role that maps to the given team?
 * Used on revoke to decide whether to take them out of the team.
 */
async function userHasAnyRoleForTeam(
  userObjectId: string,
  team: VeraTeamName,
  excludingRole: string,
): Promise<boolean> {
  const rolesForTeam = VERA_APP_ROLES.map((r) => r.value).filter(
    (v) => v !== excludingRole && teamForRole(v) === team,
  );
  if (rolesForTeam.length === 0) return false;
  const results = await Promise.all(
    rolesForTeam.map((r) => userRoleAssignmentApi.findByUserAndRole(userObjectId, r)),
  );
  return results.some(Boolean);
}

export async function grantRoleWithTeamSync(
  input: CreateUserRoleAssignmentInput,
  systemuserid: string | null,
): Promise<UserRoleAssignment> {
  const assignment = await userRoleAssignmentApi.grant(input);

  const team = teamForRole(input.roleValue);
  if (!team) return assignment;

  // Pre-provisioned grant: no systemuser yet. The BFF reconciles this on
  // the user's first sign-in (patches the row + runs team sync then).
  if (!systemuserid) {
    console.info(
      `[role sync] deferring team "${team}" sync for ${input.userPrincipalName} — pre-provisioned, will run on first sign-in.`,
    );
    return assignment;
  }

  try {
    const teamRow = await findTeamByName(team);
    if (!teamRow) {
      console.warn(
        `[role sync] team "${team}" not found in Dataverse — run provision-vera-teams. Role granted but data access not synced.`,
      );
      return assignment;
    }
    await addSystemuserToTeam(teamRow.teamid, systemuserid);
  } catch (err) {
    console.warn(
      `[role sync] failed to add user to team "${team}":`,
      err instanceof Error ? err.message : err,
    );
  }
  return assignment;
}

export async function revokeRoleWithTeamSync(
  userObjectId: string,
  roleValue: string,
  systemuserid: string | null,
): Promise<boolean> {
  const removed = await userRoleAssignmentApi.revokeByUserAndRole(userObjectId, roleValue);

  const team = teamForRole(roleValue);
  if (!team || !removed) return removed;

  // Only remove from the team if no other role the user still holds maps here.
  const stillNeedsTeam = await userHasAnyRoleForTeam(userObjectId, team, roleValue);
  if (stillNeedsTeam) return removed;

  // Pending revoke (no systemuser ever existed) — nothing to remove from the team.
  if (!systemuserid) return removed;

  try {
    const teamRow = await findTeamByName(team);
    if (!teamRow) {
      console.warn(
        `[role sync] team "${team}" not found during revoke — skipping team removal.`,
      );
      return removed;
    }
    await removeSystemuserFromTeam(teamRow.teamid, systemuserid);
  } catch (err) {
    console.warn(
      `[role sync] failed to remove user from team "${team}":`,
      err instanceof Error ? err.message : err,
    );
  }
  return removed;
}
